import { createContext, useContext, useEffect, useState, ReactNode, useRef, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useDeviceSession, DeviceSession } from "@/hooks/useDeviceSession";

interface SubscriptionInfo {
  subscribed: boolean;
  tier: "light" | "pro" | "elite" | null;
  subscriptionEnd: string | null;
  isTrial: boolean;
  hasOddsAccess: boolean;
  isFullAccessPeriod: boolean;
  daysSinceStart: number | null;
}

interface DeviceLimitState {
  isOpen: boolean;
  sessions: DeviceSession[];
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  subscription: SubscriptionInfo;
  checkSubscription: (accessToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
  deviceLimit: DeviceLimitState;
  dismissDeviceLimit: () => void;
  removeDeviceAndRetry: (sessionId: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const wasLoggedOut = useRef(true);
  const deviceCheckDone = useRef(false);
  const [deviceLimit, setDeviceLimit] = useState<DeviceLimitState>({ isOpen: false, sessions: [] });
  const { canRegisterDevice, registerDevice, removeDevice } = useDeviceSession();
  const [subscription, setSubscription] = useState<SubscriptionInfo>({
    subscribed: false,
    tier: null,
    subscriptionEnd: null,
    isTrial: false,
    hasOddsAccess: true,
    isFullAccessPeriod: false,
    daysSinceStart: null,
  });

  const checkSubscription = async (accessToken?: string) => {
    const token = accessToken || session?.access_token;
    if (!token) {
      console.log("checkSubscription: No access token available");
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (error) {
        console.log("Subscription check response:", error.message);
        return;
      }

      if (data) {
        setSubscription({
          subscribed: data.subscribed,
          tier: data.tier,
          subscriptionEnd: data.subscription_end,
          isTrial: data.is_trial,
          hasOddsAccess: data.has_odds_access ?? true,
          isFullAccessPeriod: data.is_full_access_period ?? false,
          daysSinceStart: data.days_since_start ?? null,
        });
      }
    } catch (error) {
      console.log("Subscription check failed, using default state");
    }
  };

  /** Run device check for any sign-in (magic link, OTP, OAuth) */
  const handleDeviceCheckForUser = useCallback(async (userId: string) => {
    if (deviceCheckDone.current) return;
    deviceCheckDone.current = true;

    const { allowed, existingSessions, isCurrentDeviceRegistered } = await canRegisterDevice(userId);

    if (!allowed) {
      setDeviceLimit({ isOpen: true, sessions: existingSessions });
      return;
    }

    if (!isCurrentDeviceRegistered) {
      await registerDevice(userId);
    }
  }, [canRegisterDevice, registerDevice]);

  const dismissDeviceLimit = useCallback(async () => {
    setDeviceLimit({ isOpen: false, sessions: [] });
    // Sign out since they can't use this device
    try {
      await supabase.auth.signOut();
    } catch { /* ignore */ }
    setUser(null);
    setSession(null);
  }, []);

  const removeDeviceAndRetry = useCallback(async (sessionId: string) => {
    const removed = await removeDevice(sessionId);
    if (!removed) return;

    // Re-check after removal
    const currentUser = user;
    if (currentUser) {
      deviceCheckDone.current = false;
      const { allowed, existingSessions, isCurrentDeviceRegistered } = await canRegisterDevice(currentUser.id);
      if (allowed) {
        if (!isCurrentDeviceRegistered) {
          await registerDevice(currentUser.id);
        }
        setDeviceLimit({ isOpen: false, sessions: [] });
      } else {
        setDeviceLimit({ isOpen: true, sessions: existingSessions });
      }
    }
  }, [user, canRegisterDevice, registerDevice, removeDevice]);

  const signOut = async () => {
    const currentUserId = user?.id;
    if (currentUserId) {
      const deviceId = localStorage.getItem("hockey_device_id");
      if (deviceId) {
        try {
          await supabase
            .from("user_sessions")
            .delete()
            .eq("user_id", currentUserId)
            .eq("device_id", deviceId);
        } catch (e) {
          console.log("Failed to remove device session:", e);
        }
      }
    }

    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.log("Sign out error (session may already be expired):", e);
    }

    deviceCheckDone.current = false;
    setUser(null);
    setSession(null);
    setSubscription({
      subscribed: false,
      tier: null,
      subscriptionEnd: null,
      isTrial: false,
      hasOddsAccess: true,
      isFullAccessPeriod: false,
      daysSinceStart: null,
    });
  };

  useEffect(() => {
    const keepSignedIn = localStorage.getItem("hockey_keep_signed_in");
    const sessionAlive = sessionStorage.getItem("hockey_session_alive");

    if (keepSignedIn === "false" && !sessionAlive) {
      supabase.auth.signOut().then(() => {
        setUser(null);
        setSession(null);
        setIsLoading(false);
      });
      return;
    }

    sessionStorage.setItem("hockey_session_alive", "1");

    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log("Auth state changed:", event, !!newSession);
        const wasLoggedOutBefore = wasLoggedOut.current;
        wasLoggedOut.current = !newSession;

        setSession(newSession);
        setUser(newSession?.user ?? null);
        setIsLoading(false);

        // Device check on fresh sign-in (covers OAuth, magic link, OTP)
        if (newSession?.user && wasLoggedOutBefore) {
          // Reset device check flag for new sign-ins
          deviceCheckDone.current = false;
          handleDeviceCheckForUser(newSession.user.id);

          console.log("User logged in, refreshing game data in 500ms...");
          setTimeout(() => {
            console.log("Invalidating queries now...");
            queryClient.invalidateQueries({ queryKey: ["allTodaysGames"] });
            queryClient.invalidateQueries({ queryKey: ["todaysGames"] });
            queryClient.invalidateQueries({ queryKey: ["liveGames"] });
            queryClient.invalidateQueries({ queryKey: ["schedule"] });
            queryClient.invalidateQueries({ queryKey: ["standings"] });
            queryClient.invalidateQueries({ queryKey: ["topScorers"] });
          }, 500);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      console.log("Initial session:", !!initialSession);
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setIsLoading(false);

      // Device check for existing sessions (e.g. page reload)
      if (initialSession?.user) {
        handleDeviceCheckForUser(initialSession.user.id);
      }
    });

    return () => {
      authSubscription.unsubscribe();
    };
  }, [queryClient, handleDeviceCheckForUser]);

  useEffect(() => {
    if (session?.access_token) {
      checkSubscription(session.access_token);
    }
  }, [session?.access_token]);

  useEffect(() => {
    if (!session?.access_token) return;
    const interval = setInterval(() => {
      checkSubscription(session.access_token);
    }, 60000);
    return () => clearInterval(interval);
  }, [session?.access_token]);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        subscription,
        checkSubscription,
        signOut,
        deviceLimit,
        dismissDeviceLimit,
        removeDeviceAndRetry,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
